// src/services/CustomerRiskService.js
import auditService from './AuditService';
import moduleFreshnessService from './ModuleFreshnessService';
import storage from './StorageAdapter';
import entityService from './EntityService';
import ServiceValidator from './ServiceValidator';
import { initialRisks } from '../modules/BWRA/data/riskData';
import concurrencyService from './ConcurrencyService';
import customerriskApi from '../api/customerrisk';
import { makeDualWriteMixin, applyDualWriteMixin } from './_dualWriteHelpers';

class CustomerRiskService {
  constructor() {
    this.MODELS_KEY = 'customerrisk_models';
    this.PARAMETERS_KEY = 'customerrisk_parameters';
    this.SCENARIOS_KEY = 'customerrisk_scenarios';
    this.LAST_VALUES_KEY = 'customerrisk_last_values';
    this.TEST_CASES_KEY = 'customerrisk_test_cases';
    this.SCORE_HISTORY_KEY = 'customerrisk_score_history';
    this.SCORE_HISTORY_MAX = 100;
    this.listeners = [];
    // Migrate from global keys to entity-scoped keys
    this._migrateToEntityScope();
    // Re-notify listeners when active entity changes
    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _migrateToEntityScope() {
    const migrated = storage.getItem('customerrisk_entity_scope_migrated');
    if (migrated === 'true') return;

    const entityId = entityService.getActiveEntityId();
    if (!entityId) return;

    const jsonKeys = [this.MODELS_KEY, this.PARAMETERS_KEY, this.SCENARIOS_KEY, this.LAST_VALUES_KEY];

    for (const key of jsonKeys) {
      const globalValue = storage.getJSON(key, null);
      const scopedKey = `${key}_${entityId}`;
      const scopedValue = storage.getJSON(scopedKey, null);
      if (globalValue !== null && scopedValue === null) {
        storage.setJSON(scopedKey, globalValue);
      }
    }

    storage.setItem('customerrisk_entity_scope_migrated', 'true');
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  // === MODELS ===

  getModels() {
    return storage.getJSON(this._entityKey(this.MODELS_KEY), []);
  }

  async saveModels(models) {
    await this._dualWriteJson(this.MODELS_KEY, models);
    await moduleFreshnessService.markModified('CustomerRisk');
    this.notifyListeners();
  }

  async addModel(model) {
    ServiceValidator.validate(model, {
      name: { required: true, type: 'string', minLength: 1 },
    }, 'CustomerRiskService.addModel');
    const models = this.getModels();
    models.push(model);
    await this.saveModels(models);
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'Model',
      name: model.name
    });
    return models;
  }

  async updateModel(modelName, updatedModel) {
    const models = this.getModels();
    const index = models.findIndex(m => m.name === modelName);
    if (index >= 0) {
      models[index] = updatedModel;
    } else {
      models.push(updatedModel);
    }
    await this.saveModels(models);
    return models;
  }

  async deleteModel(modelName) {
    const models = this.getModels().filter(m => m.name !== modelName);
    await this.saveModels(models);
    auditService.log('DELETE', 'CustomerRisk', {
      entity: 'Model',
      name: modelName
    });
    return models;
  }

  async cloneModel(modelName, newName) {
    const models = this.getModels();
    const original = models.find(m => m.name === modelName);
    if (!original) return models;
    const clone = {
      ...JSON.parse(JSON.stringify(original)),
      name: newName,
      version: null,
      history: [],
      isDraft: true,
      archivedVersions: [],
      // Governance: clone starts as a new draft, not inheriting approval status
      approvalStatus: 'draft',
      approvalHistory: [],
      approvedBy: null,
      approvedAt: null,
    };
    models.push(clone);
    await this.saveModels(models);
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'Model',
      name: newName,
      clonedFrom: modelName
    });
    return models;
  }

  // Create a new version of an existing approved model.
  // The current approved state is snapshotted into archivedVersions,
  // the current record becomes a draft of the bumped version,
  // and approval is reset so the new version must go through governance again.
  async newVersion(modelName, isMajor = false) {
    const models = this.getModels();
    const index = models.findIndex(m => m.name === modelName);
    if (index < 0) return models;
    const model = models[index];
    const currentVersion = model.version || { major: 1, minor: 0 };
    const bumpedVersion = isMajor
      ? { major: currentVersion.major + 1, minor: 0 }
      : { major: currentVersion.major, minor: currentVersion.minor + 1 };

    const archivedSnapshot = {
      version: currentVersion,
      parameters: model.parameters ? JSON.parse(JSON.stringify(model.parameters)) : [],
      configuration: model.configuration ? JSON.parse(JSON.stringify(model.configuration)) : null,
      documentation: model.documentation ? JSON.parse(JSON.stringify(model.documentation)) : null,
      approvalStatus: model.approvalStatus || 'draft',
      approvedBy: model.approvedBy || null,
      approvedAt: model.approvedAt || null,
      approvalHistory: [...(model.approvalHistory || [])],
      archivedAt: new Date().toISOString(),
    };

    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const newVersionHistoryEntry = {
      version: `v${bumpedVersion.major}.${bumpedVersion.minor}`,
      date: now.split('T')[0],
      updatedBy: by,
      changes: [{
        type: 'version',
        action: isMajor ? 'major_bump' : 'minor_bump',
        from: `v${currentVersion.major}.${currentVersion.minor}`,
        to: `v${bumpedVersion.major}.${bumpedVersion.minor}`,
      }],
    };

    models[index] = {
      ...model,
      version: bumpedVersion,
      approvalStatus: 'draft',
      approvedBy: null,
      approvedAt: null,
      approvalHistory: [],
      history: [...(model.history || []), newVersionHistoryEntry],
      archivedVersions: [...(model.archivedVersions || []), archivedSnapshot],
    };

    await this.saveModels(models);
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'ModelVersion',
      name: modelName,
      from: `v${currentVersion.major}.${currentVersion.minor}`,
      to: `v${bumpedVersion.major}.${bumpedVersion.minor}`,
      type: isMajor ? 'MAJOR' : 'MINOR',
      by,
    });
    return models;
  }

  // Return the operational snapshot for a model: either the current record
  // (if approved) or the most recently approved version from archivedVersions.
  // Returns null if no version of the model is approved.
  getActiveSnapshot(model) {
    if (!model) return null;
    if (model.approvalStatus === 'approved') {
      return {
        version: model.version,
        parameters: model.parameters || [],
        configuration: model.configuration || null,
        documentation: model.documentation || null,
        approvedBy: model.approvedBy,
        approvedAt: model.approvedAt,
        source: 'current',
      };
    }
    const archived = (model.archivedVersions || [])
      .filter(v => v.approvalStatus === 'approved')
      .sort((a, b) => {
        const av = a.version || { major: 0, minor: 0 };
        const bv = b.version || { major: 0, minor: 0 };
        if (av.major !== bv.major) return bv.major - av.major;
        return bv.minor - av.minor;
      });
    if (archived.length === 0) return null;
    return {
      ...archived[0],
      source: 'archived',
    };
  }

  // Get all models (current + archived versions) flattened for version history display.
  // Returns an array of { modelName, version, approvalStatus, approvedBy, approvedAt, isCurrent } entries.
  getVersionHistory(modelName) {
    const models = this.getModels();
    const model = models.find(m => m.name === modelName);
    if (!model) return [];
    const entries = [];
    (model.archivedVersions || []).forEach(archived => {
      entries.push({
        modelName,
        version: archived.version,
        approvalStatus: archived.approvalStatus,
        approvedBy: archived.approvedBy,
        approvedAt: archived.approvedAt,
        archivedAt: archived.archivedAt,
        isCurrent: false,
      });
    });
    entries.push({
      modelName,
      version: model.version,
      approvalStatus: model.approvalStatus,
      approvedBy: model.approvedBy,
      approvedAt: model.approvedAt,
      isCurrent: true,
    });
    return entries.sort((a, b) => {
      const av = a.version || { major: 0, minor: 0 };
      const bv = b.version || { major: 0, minor: 0 };
      if (av.major !== bv.major) return bv.major - av.major;
      return bv.minor - av.minor;
    });
  }

  // Get only saved (versioned) models suitable for simulation
  getSavedModels() {
    return this.getModels().filter(m => m.version !== null);
  }

  // Get only approved models (for use in Simulation and Validation).
  // Returns models with parameters/configuration/version overlaid from the active
  // approved snapshot — so a model whose current record is a draft but whose
  // archivedVersions contain an approved version is still returned with the
  // approved configuration active.
  getApprovedModels() {
    return this.getModels()
      .map(m => {
        const snap = this.getActiveSnapshot(m);
        if (!snap) return null;
        return {
          ...m,
          version: snap.version,
          parameters: snap.parameters,
          configuration: snap.configuration,
          documentation: snap.documentation || m.documentation,
          approvedBy: snap.approvedBy,
          approvedAt: snap.approvedAt,
          approvalStatus: 'approved',
          _activeFromArchived: snap.source === 'archived',
        };
      })
      .filter(Boolean);
  }

  // === APPROVAL WORKFLOW ===

  async submitForReview(modelName) {
    const models = this.getModels();
    const index = models.findIndex(m => m.name === modelName);
    if (index < 0) return models;
    const model = models[index];
    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = {
      from: model.approvalStatus || 'draft',
      to: 'submitted',
      by,
      at: now,
      comment: ''
    };
    models[index] = {
      ...model,
      approvalStatus: 'submitted',
      approvalHistory: [...(model.approvalHistory || []), historyEntry]
    };
    await this.saveModels(models);
    auditService.log('STATUS_TRANSITION', 'CustomerRisk', {
      entity: 'Model',
      name: modelName,
      from: historyEntry.from,
      to: 'submitted',
      by
    });
    return models;
  }

  async approveModel(modelName, comment = '') {
    const models = this.getModels();
    const index = models.findIndex(m => m.name === modelName);
    if (index < 0) return models;
    const model = models[index];
    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = {
      from: model.approvalStatus || 'submitted',
      to: 'approved',
      by,
      at: now,
      comment
    };
    models[index] = {
      ...model,
      approvalStatus: 'approved',
      approvedBy: by,
      approvedAt: now,
      approvalHistory: [...(model.approvalHistory || []), historyEntry]
    };
    await this.saveModels(models);
    auditService.log('STATUS_TRANSITION', 'CustomerRisk', {
      entity: 'Model',
      name: modelName,
      from: historyEntry.from,
      to: 'approved',
      by
    });
    return models;
  }

  async rejectModel(modelName, comment = '') {
    const models = this.getModels();
    const index = models.findIndex(m => m.name === modelName);
    if (index < 0) return models;
    const model = models[index];
    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = {
      from: model.approvalStatus || 'submitted',
      to: 'draft',
      by,
      at: now,
      comment
    };
    models[index] = {
      ...model,
      approvalStatus: 'draft',
      approvedBy: null,
      approvedAt: null,
      approvalHistory: [...(model.approvalHistory || []), historyEntry]
    };
    await this.saveModels(models);
    auditService.log('STATUS_TRANSITION', 'CustomerRisk', {
      entity: 'Model',
      name: modelName,
      from: historyEntry.from,
      to: 'draft',
      by
    });
    return models;
  }

  _getCurrentUser() {
    try {
      const session = storage.getJSON('rms_session', null);
      return session?.username || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  // === PARAMETERS ===

  getParameters() {
    return storage.getJSON(this._entityKey(this.PARAMETERS_KEY), []);
  }

  async saveParameters(parameters) {
    await this._dualWriteJson(this.PARAMETERS_KEY, parameters);
    await moduleFreshnessService.markModified('CustomerRisk');
    this.notifyListeners();
  }

  async addParameter(parameter) {
    ServiceValidator.validate(parameter, {
      id: { required: true, type: 'string', minLength: 1 },
      name: { required: true, type: 'string', minLength: 1 },
    }, 'CustomerRiskService.addParameter');
    const parameters = this.getParameters();
    parameters.push(parameter);
    await this.saveParameters(parameters);
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'Parameter',
      name: parameter.name
    });
    return parameters;
  }

  async updateParameter(parameterId, updatedParameter) {
    const parameters = this.getParameters();
    const index = parameters.findIndex(p => p.id === parameterId);
    if (index >= 0) {
      parameters[index] = updatedParameter;
      await this.saveParameters(parameters);
      auditService.log('UPDATE', 'CustomerRisk', {
        entity: 'Parameter',
        id: parameterId,
        name: updatedParameter.name
      });
      await concurrencyService.recordSave('customerrisk_parameter', parameterId);
    }
    return parameters;
  }

  async deleteParameter(parameterId) {
    const parameters = this.getParameters();
    const param = parameters.find(p => p.id === parameterId);
    const remaining = parameters.filter(p => p.id !== parameterId);
    await this.saveParameters(remaining);
    if (param) {
      auditService.log('DELETE', 'CustomerRisk', {
        entity: 'Parameter',
        id: parameterId,
        name: param.name
      });
    }
    return remaining;
  }

  // === SCENARIO PERSISTENCE ===

  getLastValues(modelName) {
    const all = storage.getJSON(this._entityKey(this.LAST_VALUES_KEY), {});
    return all[modelName] || {};
  }

  async saveLastValues(modelName, parameterValues) {
    const all = storage.getJSON(this._entityKey(this.LAST_VALUES_KEY), {});
    all[modelName] = parameterValues;
    await this._dualWriteJson(this.LAST_VALUES_KEY, all);
  }

  getScenarios(modelName) {
    const all = storage.getJSON(this._entityKey(this.SCENARIOS_KEY), {});
    return all[modelName] || [];
  }

  async saveScenario(modelName, scenario) {
    const all = storage.getJSON(this._entityKey(this.SCENARIOS_KEY), {});
    if (!all[modelName]) {
      all[modelName] = [];
    }
    // Replace if same name exists
    const idx = all[modelName].findIndex(s => s.name === scenario.name);
    if (idx >= 0) {
      all[modelName][idx] = scenario;
    } else {
      all[modelName].push(scenario);
    }
    await this._dualWriteJson(this.SCENARIOS_KEY, all);
    this.notifyListeners();
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'Scenario',
      name: scenario.name,
      model: modelName
    });
  }

  async deleteScenario(modelName, scenarioName) {
    const all = storage.getJSON(this._entityKey(this.SCENARIOS_KEY), {});
    if (all[modelName]) {
      all[modelName] = all[modelName].filter(s => s.name !== scenarioName);
      await this._dualWriteJson(this.SCENARIOS_KEY, all);
      this.notifyListeners();
      auditService.log('DELETE', 'CustomerRisk', {
        entity: 'Scenario',
        name: scenarioName,
        model: modelName
      });
    }
  }

  // === TEST CASE PERSISTENCE ===

  getTestCases(modelName) {
    const all = storage.getJSON(this._entityKey(this.TEST_CASES_KEY), {});
    return all[modelName] || [];
  }

  async saveTestCase(modelName, testCase) {
    const all = storage.getJSON(this._entityKey(this.TEST_CASES_KEY), {});
    if (!all[modelName]) all[modelName] = [];
    all[modelName].unshift(testCase); // newest first
    await this._dualWriteJson(this.TEST_CASES_KEY, all);
    this.notifyListeners();
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'TestCase', id: testCase.id, model: modelName
    });
  }

  async deleteTestCase(modelName, testCaseId) {
    const all = storage.getJSON(this._entityKey(this.TEST_CASES_KEY), {});
    if (all[modelName]) {
      all[modelName] = all[modelName].filter(tc => tc.id !== testCaseId);
      await this._dualWriteJson(this.TEST_CASES_KEY, all);
      this.notifyListeners();
      auditService.log('DELETE', 'CustomerRisk', {
        entity: 'TestCase', id: testCaseId, model: modelName
      });
    }
  }

  // === SCORE HISTORY ===

  getScoreHistory(modelName) {
    const all = storage.getJSON(this._entityKey(this.SCORE_HISTORY_KEY), {});
    return all[modelName] || [];
  }

  async recordScoreSnapshot(modelName, score, riskClass, reason, modelVersion) {
    ServiceValidator.requireString(modelName, 'modelName', 'CustomerRiskService.recordScoreSnapshot');
    ServiceValidator.requireNumber(score, 'score', 'CustomerRiskService.recordScoreSnapshot');
    ServiceValidator.requireString(riskClass, 'riskClass', 'CustomerRiskService.recordScoreSnapshot');
    const all = storage.getJSON(this._entityKey(this.SCORE_HISTORY_KEY), {});
    if (!all[modelName]) all[modelName] = [];
    const entry = {
      id: `sh_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      score,
      riskClass,
      reason: reason || '',
      modelVersion: modelVersion || null,
    };
    all[modelName].unshift(entry); // newest first
    if (all[modelName].length > this.SCORE_HISTORY_MAX) {
      all[modelName] = all[modelName].slice(0, this.SCORE_HISTORY_MAX);
    }
    await this._dualWriteJson(this.SCORE_HISTORY_KEY, all);
    auditService.log('CREATE', 'CustomerRisk', {
      entity: 'ScoreSnapshot',
      model: modelName,
      score,
      riskClass,
      reason,
      modelVersion,
    });
    this.notifyListeners();
    return all[modelName];
  }

  async clearScoreHistory(modelName) {
    const all = storage.getJSON(this._entityKey(this.SCORE_HISTORY_KEY), {});
    all[modelName] = [];
    await this._dualWriteJson(this.SCORE_HISTORY_KEY, all);
    this.notifyListeners();
  }

  // === BWRA RISK COVERAGE ===

  // Returns { categoryId: [{ id, name, description }] } — which BWRA categories each parameter declares it covers
  // Derives category from declaredRisks (new) or bwraCategories (legacy)
  getAllParameterCoverage() {
    const parameters = this.getParameters();
    const coverage = {};
    parameters.forEach(param => {
      const addEntry = (catId) => {
        if (!coverage[catId]) coverage[catId] = [];
        if (!coverage[catId].find(x => x.id === param.id)) {
          coverage[catId].push({ id: param.id, name: param.name, description: param.description });
        }
      };
      // New: derive category from declaredRisks
      const declaredRisks = param.declaredRisks || [];
      declaredRisks.forEach(riskId => {
        const risk = initialRisks.find(r => r.id === riskId);
        if (risk?.category) addEntry(risk.category);
      });
      // Legacy: direct bwraCategories
      const cats = param.bwraCategories || [];
      cats.forEach(catId => addEntry(catId));
    });
    return coverage;
  }

  // Returns { riskId: [{ id, name, description }] } — which specific BWRA risks each parameter declares it addresses
  getDeclaredRiskCoverage() {
    const parameters = this.getParameters();
    const coverage = {};
    parameters.forEach(param => {
      const risks = param.declaredRisks || [];
      risks.forEach(riskId => {
        if (!coverage[riskId]) coverage[riskId] = [];
        coverage[riskId].push({ id: param.id, name: param.name, description: param.description });
      });
    });
    return coverage;
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  // INFRA-181: backend dual-write + initFromBackend (BWRA-mönstret).
  async initFromBackend() {
    return this._initFromBackend(
      [this.MODELS_KEY, this.PARAMETERS_KEY, this.SCENARIOS_KEY,
       this.LAST_VALUES_KEY, this.TEST_CASES_KEY, this.SCORE_HISTORY_KEY],
      [],  // inga boolean-flags i CustomerRiskService
    );
  }
}

// Applicera dual-write-mixin (delas med BWRA-mönstret)
applyDualWriteMixin(CustomerRiskService, makeDualWriteMixin({
  storage, api: customerriskApi, serviceName: 'CustomerRiskService',
}));

const customerRiskService = new CustomerRiskService();

// Expose on window for debugging (consistent with other services)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.customerRiskService = customerRiskService;
}

export default customerRiskService;
